// The rule fields the visual audience editor offers per audience type, the value each one
// starts with, and the first thing wrong with a set of groups. Pure: the editor
// (`screens/audience-rules-editor.tsx`) renders these, and `resolveVisualAudienceRules` in
// `./audience-visual-rules` is what turns the rows into canonical API rules.

import type { Audience, Pixel, Production } from '@/api/types';
import type { SelectionOrder } from './selection-order';
import {
  AUDIENCE_AGE_RANGE_VALUES,
  AUDIENCE_BUCKET_CATALOG,
  AUDIENCE_HOUSEHOLD_HAS_CHILDREN_VALUES,
  type AudienceBucketDimension,
} from '@/api/types.gen';
import {
  ENRICHED_AUDIENCE_VALUES,
  LOOKALIKE_TOP_SHARE_BPS_STOPS,
  PIXEL_RETARGETING_ACTIONS,
  formatTopShareBps,
  isLegacyRangeValue,
  visualTypeFromAudienceType,
  type VisualAudienceGroup,
  type VisualAudienceType,
} from './audience-visual-rules';
import { purchaseWindowFromValue, purchaseWindowIssue, type PurchaseWindow } from './purchase-window';

export const WINDOWS: readonly string[] = ['7 days', '30 days', '60 days', '90 days', '6 months', '1 year', '2 years'];
export const INCOME_BANDS: readonly string[] = ENRICHED_AUDIENCE_VALUES.incomeBands;

/** The window a new purchase rule starts with. */
const DEFAULT_PURCHASE_WINDOW: PurchaseWindow = { mode: 'last', days: 730 };

/** Display labels for one bucket dimension, keyed by the value's string form. */
function bucketLabels(dimension: AudienceBucketDimension): Record<string, string> {
  return Object.fromEntries(
    AUDIENCE_BUCKET_CATALOG
      .filter((entry) => entry.dimension === dimension)
      .sort((left, right) => left.order - right.order)
      .map((entry) => [String(entry.value), entry.display_label]),
  );
}

export function dropdownOptions(
  values: readonly (string | boolean)[],
  labels: Readonly<Record<string, string>> = {},
): { id: string; label: string }[] {
  return values.map((value) => ({
    id: String(value),
    label: labels[String(value)] || String(value),
  }));
}

interface RuleFieldBase {
  id: string;
  label: string;
  op: string;
}

/** One field a rule row can be set to; `kind` picks the controls the row renders. */
export type RuleField = RuleFieldBase & (
  | { kind: 'production_window' }
  /** Last purchase: a rolling or dated window (`lib/purchase-window.ts`). */
  | { kind: 'purchase_window' }
  /** Ranked bands picked several at a time; a legacy range no band set spells stays whole. */
  | { kind: 'bands'; vals: readonly string[] }
  | { kind: 'frequency_window' }
  | { kind: 'pixel_action_window' }
  | { kind: 'audience' }
  | { kind: 'text'; placeholder: string }
  /** `order`: how the picker sorts the values (`lib/selection-order.ts`). */
  | { kind: 'multi'; vals: readonly string[]; labels?: Readonly<Record<string, string>>; order: SelectionOrder }
  | { kind: 'boolean'; vals: readonly boolean[]; labels: Readonly<Record<string, string>> }
  | { kind?: undefined; vals: readonly string[] }
);

// avg_spend, genre, DMA labels, and subscriber tier are deliberately absent:
// none can be represented truthfully by the public Rule wire type.
export const RULE_FIELDS: Readonly<Record<VisualAudienceType, readonly RuleField[]>> = {
  Direct: [
    { id: 'production_history', label: 'Production purchased', op: 'is any of', kind: 'production_window' },
    { id: 'recency', label: 'Last purchase', op: 'was', kind: 'purchase_window' },
    { id: 'frequency', label: 'Purchase freq.', op: 'is at least', kind: 'frequency_window' },
    { id: 'geo', label: 'ZIP prefix', op: 'is any of', kind: 'text', placeholder: 'e.g. 100, 112, 070' },
    { id: 'age', label: 'Age range', op: 'is any of', kind: 'bands', vals: AUDIENCE_AGE_RANGE_VALUES },
    {
      id: 'gender',
      label: 'Gender',
      op: 'is any of',
      kind: 'multi',
      vals: ENRICHED_AUDIENCE_VALUES.gender,
      labels: bucketLabels('gender'),
      order: 'alpha',
    },
    { id: 'income', label: 'Household income', op: 'is any of', kind: 'bands', vals: INCOME_BANDS },
    { id: 'credit_score', label: 'Credit score', op: 'is any of', kind: 'multi', vals: ENRICHED_AUDIENCE_VALUES.creditScore, order: 'ranked' },
    { id: 'education', label: 'Education', op: 'is any of', kind: 'multi', vals: ENRICHED_AUDIENCE_VALUES.education, order: 'ranked' },
    { id: 'religion', label: 'Religion', op: 'is any of', kind: 'multi', vals: ENRICHED_AUDIENCE_VALUES.religion, order: 'alpha' },
    {
      id: 'household_has_children',
      label: 'Household has children',
      op: 'is',
      kind: 'boolean',
      vals: AUDIENCE_HOUSEHOLD_HAS_CHILDREN_VALUES,
      labels: bucketLabels('household_has_children'),
    },
  ],
  Retargeting: [
    { id: 'pixel_action', label: 'Pixel activity', op: 'for', kind: 'pixel_action_window' },
  ],
  Lookalike: [
    { id: 'seed', label: 'Seed audience', op: 'from', kind: 'audience' },
    { id: 'simil', label: 'Top share', op: 'top', vals: LOOKALIKE_TOP_SHARE_BPS_STOPS.map(formatTopShareBps) },
  ],
};

export const TYPE_BLURB: Readonly<Record<VisualAudienceType, string>> = {
  Retargeting: 'Re-engage visitors and purchasers captured by an installed pixel.',
  Direct: 'Build from CRM: purchase history + demo + geo facets.',
  Lookalike: 'Expand reach with lookalikes seeded from a high-value audience.',
};

/** What the editor knows besides the rows: the reference lists, and the campaign's production. */
export interface AudienceRuleContext {
  preferredProductionId?: string | null;
  productions?: readonly Pick<Production, 'id' | 'name'>[];
  audiences?: readonly Pick<Audience, 'id' | 'name'>[];
  pixels?: readonly Pick<Pixel, 'id'>[];
}

/** A rule's value as a record, or null when it is not one (a string, a list, nothing). */
export function ruleRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

export function defaultRuleValue(field: RuleField, context: AudienceRuleContext = {}): unknown {
  if (field.kind === 'production_window') {
    return {
      productionIds: context.preferredProductionId
        ? [context.preferredProductionId]
        : [],
      window: DEFAULT_PURCHASE_WINDOW,
    };
  }
  if (field.kind === 'purchase_window') return DEFAULT_PURCHASE_WINDOW;
  if (field.kind === 'bands') return field.vals[0] ? [field.vals[0]] : [];
  if (field.kind === 'frequency_window') {
    return { minPurchases: 2, window: '1 year' };
  }
  if (field.kind === 'pixel_action_window') {
    return { pixelId: '', action: '', window: '30 days' };
  }
  if (field.kind === 'audience') return '';
  if (field.kind === 'multi') return field.vals[0] ? [field.vals[0]] : [];
  if (field.kind === 'boolean') return field.vals[0] ?? false;
  if (field.kind === 'text') return '';
  return field.vals[0] || '';
}

/** The groups a fresh audience of `type` starts with. The context is accepted for callers that
 * pass one; no default depends on it (see the comment on Direct). */
export function defaultGroupsForType(
  type: VisualAudienceType,
  _context: AudienceRuleContext = {},
): VisualAudienceGroup[] {
  if (type === 'Direct') {
    return [{
      id: 'segment-1',
      op: 'AND',
      // Production lists are alphabetical, not relevance-ranked. Never turn
      // their first row into an invisible audience constraint: a closed show
      // can make the initial estimate truthfully—but confusingly—zero.
      rules: [{ field: 'recency', operator: 'was', value: DEFAULT_PURCHASE_WINDOW }],
    }];
  }
  if (type === 'Retargeting') {
    return [{
      id: 'segment-1',
      op: 'AND',
      rules: [{
        field: 'pixel_action',
        operator: 'for',
        value: { pixelId: '', action: '', window: '30 days' },
      }],
    }];
  }
  return [{
    id: 'segment-1',
    op: 'AND',
    rules: [
      { field: 'seed', operator: 'from', value: '' },
      { field: 'simil', operator: 'top', value: '5 bps' },
    ],
  }];
}

export function prioritizeProduction<T extends { id: string }>(
  production: T | null | undefined,
  productions: readonly T[] = [],
): readonly T[] {
  // The same array when there is nothing to move: callers memoize on it.
  if (!production?.id) return productions;
  return [
    production,
    ...productions.filter((entry) => entry.id !== production.id),
  ];
}

/** The second line of a production option: its venue's name and its genre, whichever are known.
 * (The editor used to read `venue_name` / `house_code`, which no production carries, so every
 * option rendered with no second line.) */
export function productionOptionSublabel(
  production: Pick<Production, 'venue_id' | 'genre'>,
  venueNames: ReadonlyMap<string, string | null>,
): string | undefined {
  const venue = production.venue_id ? venueNames.get(production.venue_id) : null;
  return [venue, production.genre].filter(Boolean).join(' · ') || undefined;
}

export function eligibleLookalikeSeeds<T extends {
  audience_type: Audience['audience_type'];
  managed_by?: string | null;
}>(audiences: readonly T[] = []): T[] {
  return audiences.filter((audience) =>
    visualTypeFromAudienceType(audience.audience_type) !== 'Lookalike'
    // A system-owned row is not a seed. The recent-buyers companion is a
    // `direct` audience, so it would otherwise be offered here — and seeding a
    // lookalike from it silently welds the two together: D1's seed-reference
    // trigger then refuses to archive the companion, so turning its switch off
    // in Admin -> Config would defer forever with nothing in the UI saying why.
    && !audience.managed_by);
}

export function canAddAudienceGroup(type: VisualAudienceType): boolean {
  return type !== 'Lookalike';
}

/** A validation message and the field key it belongs to. */
export interface AudienceRuleIssue {
  key: string;
  message: string;
}

const trimmed = (value: unknown): string => String(value || '').trim();

export function firstAudienceRuleIssue(
  groups: readonly VisualAudienceGroup[] | null | undefined,
  type: VisualAudienceType,
  resolverError: Error | null = null,
  context: AudienceRuleContext = {},
): AudienceRuleIssue | null {
  if (!groups?.length) {
    return {
      key: 'audience-rules',
      message: 'Add at least one audience group to continue.',
    };
  }

  const fields = RULE_FIELDS[type];
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    const rules = groups[groupIndex]?.rules || [];
    if (rules.length === 0) {
      return {
        key: `audience-group-${groupIndex}`,
        message: `Add at least one rule to Group ${groupIndex + 1}.`,
      };
    }
    for (let ruleIndex = 0; ruleIndex < rules.length; ruleIndex += 1) {
      const rule = rules[ruleIndex]!;
      const key = `audience-rule-${groupIndex}-${ruleIndex}`;
      const field = fields.find((entry) => entry.id === rule.field);
      if (!field || rule.field === 'unsupported') {
        return { key, message: 'Remove or replace this unsupported rule.' };
      }
      const value = rule.value;
      const record = ruleRecord(value);
      if (field.kind === 'production_window') {
        const rawProductionIds = record?.productionIds ?? record?.productionId;
        const productionIds = Array.isArray(rawProductionIds)
          ? rawProductionIds
          : [rawProductionIds].filter(Boolean);
        if (productionIds.length === 0) {
          return { key, message: 'Select at least one production.' };
        }
        const issue = purchaseWindowIssue(purchaseWindowFromValue(record?.window));
        if (issue) return { key, message: issue };
      } else if (field.kind === 'purchase_window') {
        const issue = purchaseWindowIssue(purchaseWindowFromValue(value));
        if (issue) return { key, message: issue };
      } else if (field.kind === 'bands') {
        if (!isLegacyRangeValue(value) && !(Array.isArray(value) && value.length > 0)) {
          return { key, message: `Select at least one ${field.label.toLowerCase()} band.` };
        }
      } else if (field.kind === 'frequency_window') {
        const minPurchases = Number(record?.minPurchases);
        if (!Number.isInteger(minPurchases) || minPurchases <= 0) {
          return { key, message: 'Purchase frequency must be a positive whole number.' };
        }
        if (!trimmed(record?.window)) {
          return { key, message: 'Select a purchase lookback window.' };
        }
      } else if (field.kind === 'pixel_action_window') {
        const pixelId = trimmed(record?.pixelId);
        if (!pixelId) {
          return { key: `${key}-pixel`, message: 'Select a pixel.' };
        }
        if (
          context.pixels?.length
          && !context.pixels.some((pixel) => pixel.id === pixelId)
        ) {
          return {
            key: `${key}-pixel`,
            message: 'Select a pixel from the current pixel list.',
          };
        }
        if (!PIXEL_RETARGETING_ACTIONS.some((action) => action === record?.action)) {
          return {
            key: `${key}-action`,
            message: 'Select an action allowed by this pixel’s installation instructions.',
          };
        }
        if (!trimmed(record?.window)) {
          return {
            key: `${key}-window`,
            message: 'Select a pixel activity lookback window.',
          };
        }
      } else if (field.kind === 'text') {
        const parts = String(value ?? '').split(',').map((part) => part.trim()).filter(Boolean);
        if (parts.length === 0) {
          return { key, message: 'Enter at least one ZIP prefix.' };
        }
        if (parts.some((part) => !/^\d{1,5}$/.test(part))) {
          return { key, message: 'ZIP prefixes must contain 1–5 digits.' };
        }
      } else if (field.kind === 'audience') {
        if (!String(value ?? '').trim()) {
          return { key, message: 'Select a saved seed audience.' };
        }
      } else if (field.kind === 'multi') {
        const selected = Array.isArray(value) ? value : [value].filter(Boolean);
        if (selected.length === 0) {
          return { key, message: `Select at least one ${field.label.toLowerCase()} value.` };
        }
      } else if (field.id === 'simil') {
        // Basis points: 1% of the scored consumer universe is already ~2.7M people, so the
        // whole usable range sits below what a percentage can express. The ceiling mirrors
        // the shared rule validator, which is the authority. The stops are discrete: an
        // in-range value such as 3 bps is still invalid.
        const topShareBps = Number.parseInt(String(value ?? '').match(/^\s*(\d+)/)?.[1] ?? '', 10);
        if (
          !Number.isFinite(topShareBps)
          || !LOOKALIKE_TOP_SHARE_BPS_STOPS.some((stop) => stop === topShareBps)
        ) {
          return {
            key,
            message: `Top share must be one of ${LOOKALIKE_TOP_SHARE_BPS_STOPS.join(', ')} bps.`,
          };
        }
      } else if (field.kind !== 'boolean' && !String(value ?? '').trim()) {
        return { key, message: `${field.label} is required.` };
      }
    }
  }

  if (resolverError) {
    return {
      key: groups[0]?.rules?.length ? 'audience-rule-0-0' : 'audience-rules',
      message: resolverError.message || String(resolverError),
    };
  }
  return null;
}
